'use client';

import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';

import Box from '../../atoms/Box/Box';
import Dropdown from '../../atoms/Dropdown/Dropdown';
import Typography from '../../atoms/Typography/Typography';
import Header from './Header/Header';
import Navigation from './Navigation/Navigation';
import Footer from './Footer/Footer';
import Slider from './Slider/Slider';
import LanguageSelector from './LanguageSelector/LanguageSelector';
import { DashboardIcon, SettingsIcon } from './Icons';

import data from './sidebar.json';
import rolesData from './roles.json';

import styles from './Sidebar.module.scss';

interface SidebarProps {
  theme?: 'light' | 'dark';
  userName?: string;
  userEmail?: string;
  showRoleDropdown?: boolean;
  currentPath?: string;
  /** Controlled collapsed state */
  isCollapsed?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void;
  onToggleTheme?: () => void;
  onSignOut?: () => void;
  onLocaleChange?: (locale: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  theme = 'dark',
  userName = 'Johan Cruyff',
  userEmail = 'johan@lucidmotors.com',
  showRoleDropdown = false,
  currentPath: currentPathProp,
  isCollapsed: isCollapsedProp,
  onCollapseChange,
  onToggleTheme,
  onSignOut,
  onLocaleChange,
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const isControlled = isCollapsedProp !== undefined;
  const isCollapsed = isControlled ? isCollapsedProp : internalCollapsed;
  const [isHovered, setIsHovered] = useState(false);
  const [sliderType, setSliderType] = useState<'workspace' | 'settings' | null>(null);
  const [isSliderClosing, setIsSliderClosing] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string>(rolesData.data[0].id);
  const [isDashboardSelected, setIsDashboardSelected] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en-us');

  const ANIMATION_DURATION = 300;
  const isExpanded = !isCollapsed || isHovered;
  const isSliderActive = sliderType !== null || isSliderClosing;

  // Helper function to construct URL with locale prefix
  const getUrlWithLocale = useCallback(
    (path: string) => {
      if (selectedLanguage === 'en-us') {
        return path;
      }
      return `/${selectedLanguage}${path}`;
    },
    [selectedLanguage]
  );

  // Initialize locale from localStorage or URL on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check URL for locale first
    const pathMatch = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})/);
    if (pathMatch) {
      const localeFromUrl = pathMatch[1];
      setSelectedLanguage(localeFromUrl);
      localStorage.setItem('selectedLocale', localeFromUrl);
      onLocaleChange?.(localeFromUrl);
      return;
    }

    // Check localStorage
    const savedLocale = localStorage.getItem('selectedLocale');
    if (savedLocale) {
      setSelectedLanguage(savedLocale);
      onLocaleChange?.(savedLocale);
      return;
    }

    // Default to en-us
    setSelectedLanguage('en-us');
    localStorage.setItem('selectedLocale', 'en-us');
    onLocaleChange?.('en-us');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transform roles data into dropdown options
  const roleOptions = rolesData.data.map((role) => ({
    value: role.id,
    label: role.name,
  }));

  // Get the selected role's scopes
  const selectedRoleData = rolesData.data.find((role) => role.id === selectedRole);
  const allowedScopes = selectedRoleData?.scopes.map((scope) => scope.id) || [];

  // Filter workspaces based on selected role's scopes
  const filteredWorkspaces = data.data.filter((workspace) => allowedScopes.includes(workspace.id));

  // Initialize selectedWorkspace based on filtered workspaces
  const [selectedWorkspace, setSelectedWorkspace] = useState(filteredWorkspaces[0] || data.data[0]);
  const [selectedChildItem, setSelectedChildItem] = useState<string | null>(null);

  // Sync sidebar state with current URL on mount and path change
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let currentPath = currentPathProp || window.location.pathname;

    // Remove locale prefix if present for matching
    currentPath = currentPath.replace(/^\/[a-z]{2}-[a-z]{2}/, '');

    // Check if we're on the dashboard
    if (currentPath === '' || currentPath === '/') {
      setIsDashboardSelected(true);
      setSelectedChildItem(null);
      return;
    }

    // Find the matching workspace and child item
    for (const workspace of filteredWorkspaces) {
      if (workspace.childrens) {
        const matchingChild = workspace.childrens.find((child) =>
          currentPath.startsWith(child.path)
        );
        if (matchingChild) {
          setSelectedWorkspace(workspace);
          setSelectedChildItem(matchingChild.id);
          setIsDashboardSelected(false);
          return;
        }
      }
    }
  }, [filteredWorkspaces, currentPathProp]);

  const handleHamburgerClick = () => {
    const newCollapsedState = !isCollapsed;
    if (!isControlled) {
      setInternalCollapsed(newCollapsedState);
    }
    setIsHovered(sliderType !== null);
    onCollapseChange?.(newCollapsedState);
  };

  const handleMouseEnter = () => {
    if (isCollapsed && !isSliderActive) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed && !isSliderActive) {
      setIsHovered(false);
    }
  };

  const handleWorkspaceToggle = () => {
    if (sliderType === 'workspace') {
      handleCloseSlider(false);
    } else {
      setSliderType('workspace');
      if (isCollapsed) {
        setIsHovered(true);
      }
    }
  };

  const handleCloseSlider = (closeSidebar = true) => {
    setIsSliderClosing(true);
    setTimeout(() => {
      setSliderType(null);
      setIsSliderClosing(false);
      if (isCollapsed && closeSidebar) {
        setIsHovered(false);
      }
    }, ANIMATION_DURATION);
  };

  const handleSettingsToggle = () => {
    if (sliderType === 'settings') {
      handleCloseSlider(false);
    } else {
      setSliderType('settings');
      if (isCollapsed) {
        setIsHovered(true);
      }
    }
  };

  const handleWorkspaceSelect = (workspaceId: string) => {
    const workspace = filteredWorkspaces.find((w) => w.id === workspaceId);
    if (!workspace) return;

    setSelectedWorkspace(workspace);
    setIsDashboardSelected(false);

    const firstChild = workspace.childrens?.[0];
    if (firstChild) {
      setSelectedChildItem(firstChild.id);
      window.location.href = getUrlWithLocale(firstChild.path);
    }

    handleCloseSlider(false);
    setTimeout(() => {
      setIsHovered(false);
    }, ANIMATION_DURATION);
  };

  const handleNavigationItemClick = (itemId: string, path: string) => {
    setIsDashboardSelected(false);
    setSelectedChildItem(itemId);
    window.location.href = getUrlWithLocale(path);
  };

  const handleDashboardClick = () => {
    setIsDashboardSelected(true);
    setSelectedChildItem(null);
    window.location.href = getUrlWithLocale('/');
  };

  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId);
  };

  const handleLanguageSelect = (languageCode: string) => {
    setSelectedLanguage(languageCode);

    // Update localStorage
    localStorage.setItem('selectedLocale', languageCode);

    // Call parent callback if provided
    onLocaleChange?.(languageCode);

    // Update URL with locale
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;

      // For en-us, remove locale from URL; for others, add it
      if (languageCode === 'en-us') {
        // Remove locale prefix if it exists
        const newPath = currentPath.replace(/^\/[a-z]{2}-[a-z]{2}/, '');
        window.history.replaceState(null, '', (newPath || '/') + currentSearch);
      } else {
        // Add or update locale prefix
        let newPath = currentPath.replace(/^\/[a-z]{2}-[a-z]{2}/, '');
        newPath = `/${languageCode}${newPath || '/'}`;
        window.history.replaceState(null, '', newPath + currentSearch);
      }
    }
  };

  // Update selected workspace when role changes
  useEffect(() => {
    if (filteredWorkspaces.length > 0) {
      const isCurrentWorkspaceAvailable = filteredWorkspaces.some(
        (w) => w.id === selectedWorkspace.id
      );

      if (!isCurrentWorkspaceAvailable) {
        setSelectedWorkspace(filteredWorkspaces[0]);
        setSelectedChildItem(null);
        setIsDashboardSelected(true);
        window.location.href = getUrlWithLocale('/');
      }
    }
  }, [selectedRole, filteredWorkspaces, selectedWorkspace.id, getUrlWithLocale]);

  return (
    <nav
      className={classnames(styles.sidebar, {
        [styles.collapsed]: isCollapsed,
        [styles.expanded]: !isCollapsed,
        [styles.collapsedState]: isCollapsed && !isHovered,
        [styles.isHovered]: isHovered,
      })}
      aria-label="Main navigation"
      data-theme={theme}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Header
        isExpanded={isExpanded}
        isCollapsed={isCollapsed}
        isHovered={isHovered}
        theme={theme}
        onHamburgerClick={handleHamburgerClick}
      />

      <Navigation
        isExpanded={isExpanded}
        theme={theme}
        selectedWorkspace={selectedWorkspace}
        selectedChildItem={selectedChildItem}
        onWorkspaceToggle={handleWorkspaceToggle}
        onChildItemClick={handleNavigationItemClick}
      />

      <Slider
        isOpen={sliderType !== null}
        isClosing={isSliderClosing}
        isCollapsed={isCollapsed}
        theme={theme}
        onClose={handleCloseSlider}
        variant={sliderType || 'workspace'}
        selectedWorkspace={selectedWorkspace.id}
        onWorkspaceSelect={handleWorkspaceSelect}
        workspaces={filteredWorkspaces}
        onToggleTheme={onToggleTheme}
        languageSelector={
          <LanguageSelector
            theme={theme}
            selectedLanguage={selectedLanguage}
            onLanguageSelect={handleLanguageSelect}
            inSlider={true}
          />
        }
        rolesDropdown={
          showRoleDropdown ? (
            <Dropdown
              theme={theme}
              variant="list"
              size="medium"
              name="role-selector"
              options={roleOptions}
              selectedOption={selectedRole}
              label="Role"
              placeholder="Select Role"
              handleSelect={handleRoleSelect}
              gaComponent="sidebar-role-selector"
              logEvents={false}
              inSlider={true}
            />
          ) : undefined
        }
      />

      <Footer
        isExpanded={isExpanded}
        isCollapsed={isCollapsed}
        isHovered={isHovered}
        theme={theme}
        userName={userName}
        userEmail={userEmail}
        onSignOut={onSignOut}
        dashboardSection={
          <Box className={styles.dashboardSection}>
            <button
              className={classnames(styles.dashboardButton, {
                [styles.selected]: isDashboardSelected,
              })}
              onClick={handleDashboardClick}
            >
              <DashboardIcon size={20} color={theme === 'dark' ? 'white' : 'black'} />
              {isExpanded && (
                <Typography variant="body" size="small" color="white" theme={theme}>
                  Dashboard
                </Typography>
              )}
            </button>
            <button
              className={classnames(styles.dashboardButton, {
                [styles.selected]: sliderType === 'settings',
              })}
              onClick={handleSettingsToggle}
              aria-label="Open settings"
            >
              <SettingsIcon size={20} color={theme === 'dark' ? 'white' : 'black'} />
              {isExpanded && (
                <Typography variant="body" size="small" color="white" theme={theme}>
                  Settings
                </Typography>
              )}
            </button>
          </Box>
        }
      />
    </nav>
  );
};

Sidebar.displayName = 'Sidebar';

export default Sidebar;
